import type { Instance, SnapshotOut } from 'mobx-state-tree'
import { getRoot, types } from 'mobx-state-tree'

import { Consts } from '../../configs/consts'
import { sentrySetUser } from '../../configs/sentry'
import type { User } from '../../types/payload'
import { storage } from '../../utils/storage'
import type {
  LoginParams,
  LoginResponse,
  RefreshTokenResponse,
  ResetPasswordResponse,
  SafeRequestResponse,
} from '../api/api.types'
import { withSetProp } from './helpers/withSetProp'
import type { RootStore } from './RootStore'

export const AuthenticationStoreModel = types
  .model('AuthenticationStore')
  .props({
    ready: types.optional(types.boolean, false),
    user: types.maybe(types.frozen<User>()),
    token: types.maybe(types.string),
    expires: types.maybe(types.number),
  })
  .volatile<AuthenticationStoreVolatileProps>(() => ({
    refreshTokenTimer: undefined,
  }))
  .extend(withSetProp)
  .views(self => ({
    get rootStore(): RootStore {
      return getRoot<RootStore>(self)
    },
    get tokenExpired() {
      return !self.expires || Date.now() >= self.expires
    },
    get isAuthenticated() {
      return !!self.token && !this.tokenExpired
    },
  }))
  .actions(self => ({
    reset() {
      sentrySetUser(null)
      self.user = undefined
      self.token = undefined
      self.expires = undefined
      self.rootStore.api.queryClient.clear()
      self.rootStore.api.setAuthToken(undefined)
      clearTimeout(self.refreshTokenTimer)
      self.refreshTokenTimer = undefined
      if (Consts.isProd) {
        storage.clear()
      }
    },
  }))
  .actions(self => ({
    async logIn(params: LoginParams): Promise<{ ok: boolean; message: string }> {
      const response = await self.rootStore.api.safeRequest(self.rootStore.api.login, {
        email: params.email,
        password: params.password,
      })

      return this.processAuthResponse(response)
    },
    async refresh() {
      if (!self.isAuthenticated) {
        await self.rootStore.reset()
        return
      }
      const response = await self.rootStore.api.safeRequest(self.rootStore.api.refreshToken)

      const processedResponse = await this.processAuthResponse(response)
      if (!processedResponse.ok) {
        await self.rootStore.reset()
      }
    },
    async logOut() {
      await self.rootStore.api.safeRequest(self.rootStore.api.logout)
      await self.rootStore.reset()
    },
    startRefreshTokenTimer() {
      if (!self.isAuthenticated) return
      const timeout = Math.floor((self.expires! - Date.now()) * 0.9)
      self.refreshTokenTimer = setTimeout(() => void this.refresh(), timeout)
    },
    async processAuthResponse(
      response:
        | SafeRequestResponse<LoginResponse>
        | SafeRequestResponse<RefreshTokenResponse>
        | SafeRequestResponse<ResetPasswordResponse>,
    ): Promise<{ ok: boolean; message: string }> {
      if (response.ok) {
        const token = (response.data as RefreshTokenResponse).refreshedToken ?? (response.data as LoginResponse).token
        const expires = (response.data as RefreshTokenResponse).exp ?? Date.now() / 1000 + 604800
        self.rootStore.api.setAuthToken(token)
        self.setProp('token', token)
        self.setProp('user', response.data.user)
        self.setProp('expires', Math.floor(expires * 1000))
        this.startRefreshTokenTimer()
        sentrySetUser({
          id: response.data.user.id as string,
          ...response.data.user,
        })
        return { ok: true, message: 'Success' }
      } else {
        return { ok: false, message: response.message ?? 'Something went wrong.' }
      }
    },
  }))
  .actions(self => ({
    async afterAttach() {
      await self.refresh()
      self.setProp('ready', true)
    },
    beforeDestroy() {
      if (self.refreshTokenTimer) {
        clearTimeout(self.refreshTokenTimer)
        self.refreshTokenTimer = undefined
      }
    },
  }))

export interface AuthenticationStoreVolatileProps {
  refreshTokenTimer: NodeJS.Timeout | undefined
}
export interface AuthenticationStore extends Instance<typeof AuthenticationStoreModel> {}
export interface AuthenticationStoreSnapshot extends SnapshotOut<typeof AuthenticationStoreModel> {}
